import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, UploadCloud, X, CreditCard, CheckCircle2, ShieldCheck, AlertTriangle, Mail } from "lucide-react";
import { Reveal } from "@/components/motion";
import { trackEvent } from "@/utils/track";
import { EMAIL } from "@/constants/site";

const API = process.env.REACT_APP_BACKEND_URL;

const MODALITIES = [
  "General Radiography/X-ray",
  "Cone Beam CT (Head)",
  "Combine Radiography + Fluoroscopy",
  "Veterinary",
  "CT (Whole Body)",
  "Fluoroscopy",
  "Intraoral Dental",
];

const AREA_ABOVE = [
  "Unoccupiable Roof", "Occupiable Roof", "Office", "Restroom", "Patient Rooms",
  "Exam Room", "Corridor", "Employee Lounge", "Storage/Mechanical Room", "Other",
];
const AREA_BELOW = [
  "Slab", "Basement", "Office", "Restroom", "Patient Rooms",
  "Exam Room", "Corridor", "Employee Lounge", "Storage/Mechanical Room", "Other",
];
const CONSTRUCTION_TYPES = ["Standard Density Concrete", "Lightweight Concrete", "Other"];

// Conditional logic mirrors the Jotform rules exactly
const hiddenFor = (modality) => {
  const h = {
    pctHead: false, pctHeadContrast: false, pctBodyContrast: false,
    crossTable: false, chestBucky: false, fluoroTime: false, procedures: false,
  };
  switch (modality) {
    case "General Radiography/X-ray":
      h.pctHead = h.pctHeadContrast = h.pctBodyContrast = h.fluoroTime = h.procedures = true;
      break;
    case "Combine Radiography + Fluoroscopy":
      h.pctHead = h.pctHeadContrast = h.pctBodyContrast = true;
      break;
    case "Fluoroscopy":
      h.pctHead = h.pctHeadContrast = h.pctBodyContrast = h.crossTable = h.chestBucky = true;
      break;
    case "CT (Whole Body)":
      h.crossTable = h.chestBucky = h.fluoroTime = h.procedures = true;
      break;
    case "Veterinary":
    case "Intraoral Dental":
    case "Cone Beam CT (Head)":
      h.pctHead = h.pctHeadContrast = h.pctBodyContrast = h.crossTable = h.chestBucky = h.fluoroTime = h.procedures = true;
      break;
    default:
      break;
  }
  return h;
};

const emptyRoom = () => ({
  modality: "", makeModel: "", patientsPerWeek: "",
  pctHead: "", pctHeadContrast: "", pctBodyContrast: "",
  crossTable: "", chestBucky: "", fluoroTime: "", procedures: "",
  roomId: "", roomHeight: "",
  areaAbove: [], areaBelow: [],
  constructionAbove: [], thicknessAbove: "",
  constructionBelow: [], thicknessBelow: "",
});

const Label = ({ children, hint }) => (
  <div className="mb-2">
    <p className="text-sm font-semibold text-ink">{children}</p>
    {hint && <p className="mt-1 text-xs leading-relaxed text-ink/50">{hint}</p>}
  </div>
);

const TextInput = ({ value, onChange, placeholder, testid, type = "text" }) => (
  <input
    type={type}
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-full border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
  />
);

const RadioCards = ({ options, value, onChange, testid }) => (
  <div className="grid gap-2 sm:grid-cols-2" data-testid={testid}>
    {options.map((opt) => (
      <label
        key={opt}
        className={`flex cursor-pointer items-center gap-3 border px-4 py-3 text-sm transition-colors duration-150 ${
          value === opt ? "border-blueprint bg-blueprint/5 font-medium text-ink" : "border-border bg-white text-ink/75 hover:border-blueprint/50"
        }`}
      >
        <input
          type="radio"
          checked={value === opt}
          onChange={() => onChange(opt)}
          className="h-4 w-4 accent-blueprint"
        />
        {opt}
      </label>
    ))}
  </div>
);

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {["Yes", "No"].map((opt) => (
      <label
        key={opt}
        className={`flex cursor-pointer items-center gap-2 border px-5 py-2.5 text-sm transition-colors duration-150 ${
          value === opt ? "border-blueprint bg-blueprint/5 font-medium text-ink" : "border-border bg-white text-ink/75 hover:border-blueprint/50"
        }`}
      >
        <input type="radio" checked={value === opt} onChange={() => onChange(opt)} className="h-4 w-4 accent-blueprint" />
        {opt}
      </label>
    ))}
  </div>
);

const CheckboxGrid = ({ options, values, onChange, testid }) => {
  const toggle = (opt) =>
    onChange(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt]);
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid={testid}>
      {options.map((opt) => (
        <label
          key={opt}
          className={`flex cursor-pointer items-center gap-3 border px-4 py-2.5 text-sm transition-colors duration-150 ${
            values.includes(opt) ? "border-blueprint bg-blueprint/5 font-medium text-ink" : "border-border bg-white text-ink/75 hover:border-blueprint/50"
          }`}
        >
          <input type="checkbox" checked={values.includes(opt)} onChange={() => toggle(opt)} className="h-4 w-4 accent-blueprint" />
          {opt}
        </label>
      ))}
    </div>
  );
};

const RoomForm = ({ room, index, total, onChange }) => {
  const h = hiddenFor(room.modality);
  const set = (key) => (val) => onChange({ ...room, [key]: val });
  return (
    <div className="flex flex-col gap-7" data-testid={`room-form-${index}`}>
      <div>
        <p className="eyebrow">Room {index + 1} of {total}</p>
        <h2 className="mt-3 font-heading text-xl font-extrabold tracking-tight text-ink">Room {index + 1} Information</h2>
      </div>

      <div>
        <Label>Modality</Label>
        <RadioCards options={MODALITIES} value={room.modality} onChange={set("modality")} testid={`room-${index}-modality`} />
      </div>

      <div>
        <Label>Equipment Make and Model</Label>
        <TextInput value={room.makeModel} onChange={set("makeModel")} testid={`room-${index}-make-model`} />
      </div>

      <div>
        <Label hint="If unsure, use a reasonable overestimate or leave this blank.">Expected number of patients per week</Label>
        <TextInput value={room.patientsPerWeek} onChange={set("patientsPerWeek")} testid={`room-${index}-patients`} />
      </div>

      {!h.pctHead && (
        <div>
          <Label hint="If you are unsure, leave this blank.">Percentage of all scans on the head</Label>
          <TextInput value={room.pctHead} onChange={set("pctHead")} placeholder="ex: 40" testid={`room-${index}-pct-head`} />
        </div>
      )}
      {!h.pctHeadContrast && (
        <div>
          <Label hint="If you are unsure, leave this blank.">Percentage of all head scans that use contrast. (Multi-phase)</Label>
          <TextInput value={room.pctHeadContrast} onChange={set("pctHeadContrast")} placeholder="ex: 40" testid={`room-${index}-pct-head-contrast`} />
        </div>
      )}
      {!h.pctBodyContrast && (
        <div>
          <Label hint="If you are unsure, leave this blank.">Percentage of all body scans that use contrast. (Multi-phase)</Label>
          <TextInput value={room.pctBodyContrast} onChange={set("pctBodyContrast")} placeholder="ex: 40" testid={`room-${index}-pct-body-contrast`} />
        </div>
      )}

      {!h.crossTable && (
        <div>
          <Label>Are cross-table exams performed?</Label>
          <YesNo value={room.crossTable} onChange={set("crossTable")} testid={`room-${index}-cross-table`} />
        </div>
      )}
      {!h.chestBucky && (
        <div>
          <Label>Are chest bucky exams performed?</Label>
          <YesNo value={room.chestBucky} onChange={set("chestBucky")} testid={`room-${index}-chest-bucky`} />
        </div>
      )}

      {!h.fluoroTime && (
        <div>
          <Label hint="If you are unsure, leave this blank">Average Fluoro on Time Per Case (if known)</Label>
          <TextInput value={room.fluoroTime} onChange={set("fluoroTime")} testid={`room-${index}-fluoro-time`} />
        </div>
      )}
      {!h.procedures && (
        <div>
          <Label>Type of procedures</Label>
          <TextInput value={room.procedures} onChange={set("procedures")} testid={`room-${index}-procedures`} />
        </div>
      )}

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label hint="Example: X-ray room, NM Room, CT Room">Room ID:</Label>
          <TextInput value={room.roomId} onChange={set("roomId")} testid={`room-${index}-room-id`} />
        </div>
        <div>
          <Label hint={'Enter the floor-to-floor height, for example: 10 ft 6 in. If unknown, enter "I don\'t know."'}>Room height (feet and inches)</Label>
          <TextInput value={room.roomHeight} onChange={set("roomHeight")} testid={`room-${index}-room-height`} />
        </div>
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label>Area above room</Label>
          <CheckboxGrid options={AREA_ABOVE} values={room.areaAbove} onChange={set("areaAbove")} testid={`room-${index}-area-above`} />
        </div>
        <div>
          <Label>Area below room</Label>
          <CheckboxGrid options={AREA_BELOW} values={room.areaBelow} onChange={set("areaBelow")} testid={`room-${index}-area-below`} />
        </div>
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label>Construction between this room and the room above</Label>
          <CheckboxGrid options={CONSTRUCTION_TYPES} values={room.constructionAbove} onChange={set("constructionAbove")} testid={`room-${index}-construction-above`} />
        </div>
        <div>
          <Label>Thickness of Building Material Above (inches)</Label>
          <TextInput value={room.thicknessAbove} onChange={set("thicknessAbove")} testid={`room-${index}-thickness-above`} />
        </div>
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label>Construction between this room and the room below</Label>
          <CheckboxGrid options={CONSTRUCTION_TYPES} values={room.constructionBelow} onChange={set("constructionBelow")} testid={`room-${index}-construction-below`} />
        </div>
        <div>
          <Label>Thickness of Building Material Below (inches)</Label>
          <TextInput value={room.thicknessBelow} onChange={set("thicknessBelow")} testid={`room-${index}-thickness-below`} />
        </div>
      </div>
    </div>
  );
};

const OrderSummary = ({ roomCount, total, paid }) => (
  <div className="border border-blueprint/30 bg-blueprint/5 p-6" data-testid="wizard-order-summary">
    <p className="font-mono text-xs uppercase tracking-[0.22em] text-ink/50">{paid ? "Payment" : "Order summary"}</p>
    <div className="mt-4 flex items-baseline justify-between border-b border-blueprint/15 pb-4">
      <p className="text-sm text-ink/75">
        Shielding design — single room × <span className="font-semibold text-ink">{roomCount}</span>
      </p>
      <p className="font-heading text-sm font-bold text-ink">${(950).toLocaleString()} × {roomCount}</p>
    </div>
    <div className="mt-4 flex items-baseline justify-between">
      <p className="text-sm font-semibold text-ink">{paid ? "Total" : "Total due today"}</p>
      <p className="font-heading text-2xl font-extrabold tracking-tight text-blueprint" data-testid="wizard-total">${total.toLocaleString()}</p>
    </div>
    {paid ? (
      <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-blueprint" data-testid="wizard-paid-confirmation">
        <CheckCircle2 size={15} /> Paid in full
      </p>
    ) : (
      <p className="mt-3 text-xs leading-relaxed text-ink/55">
        Secure card checkout by Stripe. Sales tax calculated at checkout. Covered by the 90-day money-back guarantee and 5 years of free recalculations.
      </p>
    )}
  </div>
);

const RequestWizard = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState(sessionId ? "loading" : "pay");
  const [payStep, setPayStep] = useState(0); // 0 = client details, 1 = rooms & payment
  const [step, setStep] = useState(0);
  const [roomCount, setRoomCount] = useState(1);
  const [facility, setFacility] = useState({ name: "", address: "", contactName: "", email: "", phone: "" });
  const [rooms, setRooms] = useState([emptyRoom()]);
  const [files, setFiles] = useState([]);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState(null);

  // Intake (post-payment): facility was captured before payment — only legacy
  // paid drafts created before lead-capture need the facility step here
  const needsFacility = phase === "intake" && !facility.email;
  const off = needsFacility ? 1 : 0;
  const totalSteps = roomCount + 1 + off; // one step per room + files & review (+ facility for legacy drafts)
  const isRoomStep = step >= off && step < off + roomCount;
  const roomIdx = step - off;
  const isFinal = step === totalSteps - 1;
  const total = 950 * roomCount;

  const resume = async () => {
    setPhase("loading");
    setError("");
    try {
      const res = await fetch(`${API}/api/requests/by-session/${sessionId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not load your request.");
      if (data.payment_status !== "paid") {
        setPhase("unpaid");
        return;
      }
      setRequestId(data.request_id);
      setRoomCount(data.room_count);
      setFacility((f) => ({ ...f, ...(data.facility || {}) }));
      setRooms(Array.from({ length: data.room_count }, () => emptyRoom()));
      setPhase(data.intake_submitted ? "already" : "intake");
    } catch (e) {
      setError(e.message || "Could not load your request.");
      setPhase("error");
    }
  };

  useEffect(() => {
    if (sessionId) {
      resume();
    } else {
      setPhase("pay");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const setRoom = (i) => (r) => setRooms((prev) => prev.map((x, j) => (j === i ? r : x)));
  const setFac = (key) => (val) => setFacility((f) => ({ ...f, [key]: val }));

  const validateFacility = () => {
    if (!facility.name.trim()) return "Facility name is required.";
    if (!facility.contactName.trim()) return "Contact person name is required.";
    if (!/.+@.+\..+/.test(facility.email)) return "A valid contact email is required.";
    return "";
  };

  const validate = () => {
    if (needsFacility && step === 0) return validateFacility();
    if (isRoomStep && !rooms[roomIdx].modality) {
      return "Please select the modality for this room.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    setError(err);
    if (err) return;
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setError("");
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    setFiles((prev) => [...prev, ...incoming].slice(0, 10));
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, j) => j !== i));

  const continueFromDetails = async () => {
    const err = validateFacility();
    setError(err);
    if (err) return;
    setSubmitting(true);
    try {
      if (!requestId) {
        // Capture the lead immediately — before rooms and payment
        const res = await fetch(`${API}/api/requests/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ facility, origin_url: window.location.origin }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Could not start your request");
        setRequestId(data.request_id);
      } else {
        const res = await fetch(`${API}/api/requests/${requestId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ facility }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || "Could not save your details");
        }
      }
      setPayStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message || "Something went wrong — please try again.");
    }
    setSubmitting(false);
  };

  const payNow = async () => {
    if (!roomCount || roomCount < 1 || roomCount > 100) {
      setError("Enter a number of rooms between 1 and 100.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const upd = await fetch(`${API}/api/requests/${requestId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ facility, room_count: roomCount }),
      });
      const udata = await upd.json();
      if (!upd.ok) throw new Error(udata.detail || "Could not save your project details");
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key: "shielding_design_single_room",
          quantity: roomCount,
          request_id: requestId,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Checkout failed");
      trackEvent("pay_click");
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message || "Something went wrong — please try again.");
      setSubmitting(false);
    }
  };

  const submitIntake = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (!filesUploaded && files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const up = await fetch(`${API}/api/requests/${requestId}/files`, { method: "POST", body: fd });
        if (!up.ok) {
          const d = await up.json().catch(() => ({}));
          throw new Error(d.detail || "File upload failed");
        }
        setFilesUploaded(true);
      }
      const res = await fetch(`${API}/api/requests/${requestId}/intake`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ facility, rooms }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      trackEvent("intake_submitted");
      setPhase("submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message || "Something went wrong — please try again.");
    }
    setSubmitting(false);
  };

  // ---------- Phase: securing payment first ----------

  if (phase === "loading") {
    return (
      <div className="border border-border bg-white px-6 py-16 md:px-8" data-testid="request-wizard">
        <p className="flex items-center justify-center gap-3 text-sm text-ink/60" data-testid="wizard-loading">
          <Loader2 size={18} className="animate-spin text-blueprint" /> Loading your request…
        </p>
      </div>
    );
  }

  if (phase === "unpaid") {
    return (
      <div className="border border-border bg-white px-6 py-12 md:px-8" data-testid="request-wizard">
        <div className="flex items-start gap-3" data-testid="wizard-unpaid">
          <AlertTriangle size={18} className="mt-0.5 text-amber-500" />
          <div>
            <p className="font-heading text-base font-bold text-ink">We couldn't confirm your payment yet.</p>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/60">
              If you just completed checkout, it can take a few seconds to register. Otherwise your card was not charged.
            </p>
            <button
              onClick={resume}
              data-testid="wizard-recheck-button"
              className="mt-5 bg-blueprint px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
            >
              Re-check payment
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="border border-border bg-white px-6 py-12 md:px-8" data-testid="request-wizard">
        <div className="flex items-start gap-3" data-testid="wizard-load-error">
          <AlertTriangle size={18} className="mt-0.5 text-amber-500" />
          <div>
            <p className="font-heading text-base font-bold text-ink">We couldn't open this request.</p>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/60">{error}</p>
            <a href="/request" data-testid="wizard-restart-link" className="mt-5 inline-flex items-center gap-2 bg-blueprint px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink">
              Start a new request <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "already") {
    return (
      <div className="border border-blueprint/30 bg-white px-6 py-12 md:px-8" data-testid="request-wizard">
        <div data-testid="wizard-already-submitted">
          <CheckCircle2 size={26} className="text-blueprint" />
          <h2 className="mt-4 font-heading text-xl font-extrabold tracking-tight text-ink">This request is already submitted.</h2>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink/60">
            Your intake form and payment are in. Need to change something? Email{" "}
            <a href="mailto:Lancer@heartlandphysics.com" className="font-semibold text-blueprint">Lancer@heartlandphysics.com</a>.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "submitted") {
    return (
      <div className="border border-blueprint/30 bg-white px-6 py-12 md:px-8" data-testid="request-wizard">
        <div data-testid="wizard-submitted">
          <CheckCircle2 size={26} className="text-blueprint" />
          <h2 className="mt-4 font-heading text-xl font-extrabold tracking-tight text-ink">Request submitted.</h2>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink/70">
            Thank you — your intake is complete and your payment is confirmed. A confirmation email is on its way
            to {facility.email || "your inbox"}.
          </p>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink/70">
            Heartland Physics responds within one business day. Your shielding report follows within five business
            days of complete project information.
          </p>
          <p className="mt-6 flex items-center gap-2 border-t border-border pt-5 text-xs text-ink/55">
            <ShieldCheck size={14} className="text-blueprint" />
            Covered by the 90-day money-back guarantee and 5 years of free recalculations.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "pay") {
    return (
      <div className="border border-border bg-white" data-testid="request-wizard">
        <div className="border-b border-border px-6 py-4 md:px-8">
          <div className="flex items-center justify-between">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50" data-testid="wizard-progress-label">
              Step {payStep + 1} of 2
            </p>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-blueprint">
              {payStep === 0 ? "Your details" : "Rooms & payment"}
            </p>
          </div>
          <div className="mt-3 h-1 w-full bg-border">
            <div className="h-1 bg-blueprint transition-all duration-500" style={{ width: payStep === 0 ? "50%" : "100%" }} />
          </div>
        </div>

        <div className="px-6 py-8 md:px-8 md:py-10">
          {payStep === 0 && (
            <Reveal y={10}>
              <div className="flex max-w-lg flex-col gap-7" data-testid="wizard-step-details">
                <div>
                  <p className="eyebrow">Getting started</p>
                  <h2 className="mt-3 font-heading text-xl font-extrabold tracking-tight text-ink">Tell us about your project</h2>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">
                    A few contact details first — then choose your rooms and prepay securely to book your project.
                  </p>
                </div>
                <div>
                  <Label>Facility name</Label>
                  <TextInput value={facility.name} onChange={setFac("name")} testid="wizard-facility-name" />
                </div>
                <div>
                  <Label hint="Street, city, state, and ZIP.">Facility address</Label>
                  <TextInput value={facility.address} onChange={setFac("address")} testid="wizard-facility-address" />
                </div>
                <div>
                  <Label>Contact person name</Label>
                  <TextInput value={facility.contactName} onChange={setFac("contactName")} testid="wizard-contact-name" />
                </div>
                <div>
                  <Label>Contact email</Label>
                  <TextInput type="email" value={facility.email} onChange={setFac("email")} testid="wizard-contact-email" />
                </div>
                <div>
                  <Label>Contact phone number</Label>
                  <TextInput type="tel" value={facility.phone} onChange={setFac("phone")} testid="wizard-contact-phone" />
                </div>
              </div>
            </Reveal>
          )}

          {payStep === 1 && (
            <Reveal y={10}>
              <div className="flex flex-col gap-8" data-testid="wizard-step-pay">
                <div className="max-w-lg">
                  <p className="eyebrow">Rooms & payment</p>
                  <h2 className="mt-3 font-heading text-xl font-extrabold tracking-tight text-ink">How many rooms need shielding?</h2>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">
                    Prepay securely to book your project — then complete the intake form with per-room details
                    and floor plans. Each room gets its own set of questions.
                  </p>
                  <p className="mt-4 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ink/50" data-testid="wizard-prefer-email">
                    <Mail size={13} className="shrink-0 text-blueprint" />
                    Prefer email?
                    <a
                      href={`mailto:${EMAIL}?subject=Shielding%20Design%20Project`}
                      data-testid="wizard-prefer-email-link"
                      className="font-semibold text-blueprint transition-colors hover:text-ink"
                    >
                      Send your project details to {EMAIL}
                    </a>
                  </p>
                  <div className="mt-7">
                    <Label hint="Enter a number from 1 to 100.">Number of rooms</Label>
                    <TextInput
                      type="number"
                      value={roomCount}
                      onChange={(v) => setRoomCount(Math.max(1, Math.min(100, parseInt(v || "1", 10) || 1)))}
                      testid="wizard-room-count-input"
                    />
                  </div>
                </div>

                <OrderSummary roomCount={roomCount} total={total} paid={false} />
              </div>
            </Reveal>
          )}

          {error && (
            <p className="mt-6 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="wizard-error">{error}</p>
          )}

          <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
            {payStep === 1 ? (
              <button
                onClick={() => { setError(""); setPayStep(0); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={submitting}
                data-testid="wizard-pay-back-button"
                className="flex items-center gap-2 border border-border px-5 py-3 text-sm font-semibold text-ink transition-colors hover:border-blueprint hover:text-blueprint disabled:opacity-40"
              >
                <ArrowLeft size={15} /> Back
              </button>
            ) : (
              <span />
            )}
            {payStep === 0 ? (
              <button
                onClick={continueFromDetails}
                disabled={submitting}
                data-testid="wizard-details-continue-button"
                className="group flex items-center gap-2 bg-blueprint px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                {submitting ? "Saving…" : "Continue"}
                {!submitting && <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />}
              </button>
            ) : (
              <button
                onClick={payNow}
                disabled={submitting}
                data-testid="wizard-pay-button"
                className="group flex items-center gap-2 bg-blueprint px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {submitting ? "Opening secure checkout…" : `Pay $${total.toLocaleString()} securely`}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Phase: intake form after payment ----------

  return (
    <div className="border border-border bg-white" data-testid="request-wizard">
      {/* progress */}
      <div className="border-b border-border px-6 py-4 md:px-8">
        <div className="flex items-center justify-between">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50" data-testid="wizard-progress-label">
            Step {step + 1} of {totalSteps}
          </p>
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-blueprint">
            <span className="flex items-center gap-1" data-testid="wizard-paid-badge">
              <CheckCircle2 size={12} /> Paid
            </span>
            <span className="text-ink/30">·</span>
            {needsFacility && step === 0 ? "Facility" : isRoomStep ? `Room ${roomIdx + 1}` : "Files & review"}
          </p>
        </div>
        <div className="mt-3 h-1 w-full bg-border">
          <div
            className="h-1 bg-blueprint transition-all duration-500"
            style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <div className="px-6 py-8 md:px-8 md:py-10">
        {needsFacility && step === 0 && (
          <Reveal y={10}>
            <div className="flex max-w-lg flex-col gap-7" data-testid="wizard-step-facility">
              <div>
                <p className="eyebrow">Facility</p>
                <h2 className="mt-3 font-heading text-xl font-extrabold tracking-tight text-ink">Facility Information</h2>
              </div>
              <div>
                <Label>Facility name</Label>
                <TextInput value={facility.name} onChange={setFac("name")} testid="wizard-facility-name" />
              </div>
              <div>
                <Label hint="Street, city, state, and ZIP.">Facility address</Label>
                <TextInput value={facility.address} onChange={setFac("address")} testid="wizard-facility-address" />
              </div>
              <div>
                <Label>Contact person name</Label>
                <TextInput value={facility.contactName} onChange={setFac("contactName")} testid="wizard-contact-name" />
              </div>
              <div>
                <Label>Contact email</Label>
                <TextInput type="email" value={facility.email} onChange={setFac("email")} testid="wizard-contact-email" />
              </div>
              <div>
                <Label>Contact phone number</Label>
                <TextInput type="tel" value={facility.phone} onChange={setFac("phone")} testid="wizard-contact-phone" />
              </div>
            </div>
          </Reveal>
        )}

        {isRoomStep && (
          <RoomForm room={rooms[roomIdx]} index={roomIdx} total={roomCount} onChange={setRoom(roomIdx)} />
        )}

        {isFinal && (
          <Reveal y={10}>
            <div className="flex flex-col gap-8" data-testid="wizard-step-final">
              <div>
                <p className="eyebrow">Almost done</p>
                <h2 className="mt-3 font-heading text-xl font-extrabold tracking-tight text-ink">Floor plans & review</h2>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink/60">
                  Upload floor plans if you have them, then submit. Your payment is already complete.
                </p>
              </div>

              <div>
                <Label hint="Upload floor plans or drawings for your rooms — PDF, images, DWG or DXF, up to 25 MB each (max 10 files). Optional, but it speeds up your design.">
                  Upload floor plans
                </Label>
                <label
                  className="flex cursor-pointer flex-col items-center gap-3 border border-dashed border-blueprint/40 bg-blueprint/5 px-6 py-10 text-center transition-colors hover:border-blueprint"
                  data-testid="wizard-file-dropzone"
                >
                  <UploadCloud size={24} className="text-blueprint" />
                  <span className="text-sm font-medium text-ink">Click to choose files</span>
                  <span className="text-xs text-ink/50">PDF · PNG · JPG · DWG · DXF</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.dwg,.dxf"
                    className="hidden"
                    data-testid="wizard-file-input"
                    onChange={(e) => addFiles(e.target.files)}
                  />
                </label>
                {files.length > 0 && (
                  <div className="mt-4 border border-border" data-testid="wizard-file-list">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-border px-4 py-3 last:border-b-0">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 size={15} className="text-blueprint" />
                          <span className="text-sm text-ink/80">{f.name}</span>
                          <span className="font-mono text-xs text-ink/40">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                        <button onClick={() => removeFile(i)} data-testid={`wizard-file-remove-${i}`} className="text-ink/40 transition-colors hover:text-ink" aria-label={`Remove ${f.name}`}>
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <OrderSummary roomCount={roomCount} total={total} paid={true} />
            </div>
          </Reveal>
        )}

        {error && (
          <p className="mt-6 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="wizard-error">{error}</p>
        )}

        <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <button
            onClick={back}
            disabled={step === 0 || submitting}
            data-testid="wizard-back-button"
            className="flex items-center gap-2 border border-border px-5 py-3 text-sm font-semibold text-ink transition-colors hover:border-blueprint hover:text-blueprint disabled:opacity-40"
          >
            <ArrowLeft size={15} /> Back
          </button>
          {isFinal ? (
            <button
              onClick={submitIntake}
              disabled={submitting}
              data-testid="wizard-submit-button"
              className="group flex items-center gap-2 bg-blueprint px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {submitting ? "Submitting…" : "Submit request"}
            </button>
          ) : (
            <button
              onClick={next}
              data-testid="wizard-continue-button"
              className="group flex items-center gap-2 bg-blueprint px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
            >
              {isRoomStep && roomIdx === roomCount - 1 ? "Continue to files & review" : "Continue"}
              <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestWizard;
